import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';

// --- Request DTOs ---

export interface BugReportScreenshotItem {
  base64: string;
  fileName?: string;
  attachmentType: string; // 'screenshot' | 'paste' | 'upload'
}

export interface AnalyzeBugScreenshotRequest {
  screenshotBase64: string;
  userDescription?: string;
  currentUrl?: string;
  consoleErrors?: string;
  networkErrors?: string;
}

export interface SubmitBugReportRequest {
  title: string;
  description: string;
  /** @deprecated Use screenshots array instead */
  screenshotBase64?: string;
  screenshots?: BugReportScreenshotItem[];
  currentUrl?: string;
  moduleName?: string;
  browserInfo?: string;
  screenResolution?: string;
  consoleErrors?: string;
  networkErrors?: string;
  severity: string; // 'low' | 'medium' | 'high' | 'critical'
  createFreshdeskTicket?: boolean;
  aiAnalysis?: string;
  claudeCodePrompt?: string;
}

export interface TestFreshdeskConnectionRequest {
  apiUrl: string;
  apiKey: string;
}

// --- Response DTOs ---

export interface AnalyzeBugScreenshotResponse {
  success: boolean;
  aiAnalysis?: string;
  suggestedTitle?: string;
  suggestedSeverity?: string;
  detectedModule?: string;
  claudeCodePrompt?: string;
  tokensUsed: number;
}

export interface SubmitBugReportResponse {
  bugReportId: number;
  freshdeskTicketId?: number;
  freshdeskTicketUrl?: string;
  emailFallbackSent?: boolean;
  aiAnalysis?: string;
  claudeCodePrompt?: string;
}

export interface SubmitBugReportViaEmailResponse {
  emailSent: boolean;
  message?: string;
}

export interface BugReportListDto {
  bugReportID: number;
  title: string;
  severity: string;
  status: string;
  freshdeskTicketId?: number;
  freshdeskTicketUrl?: string;
  createdDate: string;
}

export interface BugReportDetailDto {
  bugReportID: number;
  employeeNo: number;
  title: string;
  description?: string;
  currentUrl?: string;
  moduleName?: string;
  browserInfo?: string;
  screenResolution?: string;
  consoleErrors?: string;
  networkErrors?: string;
  aiAnalysis?: string;
  claudeCodePrompt?: string;
  severity: string;
  status: string;
  freshdeskTicketId?: number;
  freshdeskTicketUrl?: string;
  createdDate: string;
  modifiedDate?: string;
  attachments: BugReportAttachmentDto[];
}

export interface BugReportAttachmentDto {
  attachmentID: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  blobFileName?: string;
  attachmentType: string;
  createdDate: string;
}

// --- Admin DTOs ---

export interface BugReportAdminListDto {
  bugReportID: number;
  employeeNo: number;
  title: string;
  moduleName?: string;
  severity: string;
  status: string;
  freshdeskTicketId?: number;
  freshdeskTicketUrl?: string;
  createdDate: string;
  reportedByName?: string;
}

export interface BugReportAdminPagedResult {
  items: BugReportAdminListDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface UpdateBugReportStatusRequest {
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class BugReportService {
  private get baseUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/KAIBugReport`;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {}

  /**
   * Analyze a screenshot with AI before submitting
   */
  analyzeScreenshot(request: AnalyzeBugScreenshotRequest): Observable<AnalyzeBugScreenshotResponse> {
    return this.http.post<any>(`${this.baseUrl}/analyze`, request).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'AI-analyse feilet');
      })
    );
  }

  /**
   * Submit a full bug report
   */
  submit(request: SubmitBugReportRequest): Observable<SubmitBugReportResponse> {
    return this.http.post<any>(`${this.baseUrl}/submit`, request).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'Innsending feilet');
      })
    );
  }

  /**
   * Submit bug report via email fallback (when primary submit fails)
   */
  submitViaEmail(request: SubmitBugReportRequest): Observable<SubmitBugReportViaEmailResponse> {
    return this.http.post<any>(`${this.baseUrl}/submit-email`, request).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'E-post-sending feilet');
      })
    );
  }

  /**
   * Get paginated list of own bug reports
   */
  getList(page: number = 1, pageSize: number = 20): Observable<BugReportListDto[]> {
    return this.http.get<any>(`${this.baseUrl}/list?page=${page}&pageSize=${pageSize}`).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result || [];
        }
        return [];
      })
    );
  }

  /**
   * Get a single bug report with attachments
   */
  getDetail(bugReportId: number): Observable<BugReportDetailDto> {
    return this.http.get<any>(`${this.baseUrl}/${bugReportId}`).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'Kunne ikke hente feilrapport');
      })
    );
  }

  /**
   * Test Freshdesk API connection (admin only)
   */
  testFreshdeskConnection(request: TestFreshdeskConnectionRequest): Observable<boolean> {
    return this.http.post<any>(`${this.baseUrl}/test-freshdesk`, request).pipe(
      map((response: any) => response?.success && response?.result)
    );
  }

  // ── Admin endpoints ──

  /**
   * Get paginated list of ALL bug reports (admin only)
   */
  getAdminList(page: number = 1, pageSize: number = 50, status?: string): Observable<BugReportAdminPagedResult> {
    let url = `${this.baseUrl}/admin/list?page=${page}&pageSize=${pageSize}`;
    if (status) {
      url += `&status=${status}`;
    }
    return this.http.get<any>(url).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'Kunne ikke hente feilrapporter');
      })
    );
  }

  /**
   * Get a single bug report with full details (admin only)
   */
  getAdminDetail(bugReportId: number): Observable<BugReportDetailDto> {
    return this.http.get<any>(`${this.baseUrl}/admin/${bugReportId}`).pipe(
      map((response: any) => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.message || 'Kunne ikke hente feilrapport');
      })
    );
  }

  /**
   * Update bug report status (admin only)
   */
  updateStatus(bugReportId: number, status: string): Observable<boolean> {
    return this.http.put<any>(`${this.baseUrl}/admin/${bugReportId}/status`, { status }).pipe(
      map((response: any) => {
        if (response?.success) {
          return true;
        }
        throw new Error(response?.message || 'Kunne ikke oppdatere status');
      })
    );
  }
}
